import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Filter, DollarSign, Calendar, Building2, Package } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

// Mock data for initial frontend development
const MOCK_OPPORTUNITIES = [
  {
    id: "opp-001",
    customer_name: "Whole Foods Market",
    customer_id: "cust-001",
    description: "National distribution deal for organic burritos",
    stage: "Negotiation",
    total_revenue: 450000,
    product_type: "Frozen Foods",
    business_type: "Retail",
    close_probability: 75,
    expected_close_date: "2025-02-15",
    created_at: "2024-11-15",
    owner_name: "John Doe",
    broker_name: "ABC Brokers"
  },
  {
    id: "opp-002",
    customer_name: "Costco Wholesale",
    customer_id: "cust-002",
    description: "Club pack expansion program",
    stage: "Proposal",
    total_revenue: 680000,
    product_type: "Frozen Foods",
    business_type: "Wholesale",
    close_probability: 60,
    expected_close_date: "2025-03-01",
    created_at: "2024-12-01",
    owner_name: "John Doe",
    broker_name: null
  },
  {
    id: "opp-003",
    customer_name: "Target Corporation",
    customer_id: "cust-003",
    description: "Regional test market program",
    stage: "Discovery",
    total_revenue: 225000,
    product_type: "Frozen Foods",
    business_type: "Retail",
    close_probability: 40,
    expected_close_date: "2025-04-01",
    created_at: "2024-12-15",
    owner_name: "Jane Smith",
    broker_name: "XYZ Distribution"
  },
  {
    id: "opp-004",
    customer_name: "Kroger",
    customer_id: "cust-004",
    description: "Private label partnership",
    stage: "Lead",
    total_revenue: 320000,
    product_type: "Frozen Foods",
    business_type: "Retail",
    close_probability: 25,
    expected_close_date: "2025-05-01",
    created_at: "2025-01-05",
    owner_name: "John Doe",
    broker_name: null
  },
  {
    id: "opp-005",
    customer_name: "Sysco Foods",
    customer_id: "cust-005",
    description: "Food service distribution agreement",
    stage: "Closed - Won",
    total_revenue: 350000,
    product_type: "Frozen Foods",
    business_type: "Food Service",
    close_probability: 100,
    expected_close_date: "2025-01-10",
    created_at: "2024-10-01",
    owner_name: "Jane Smith",
    broker_name: "ABC Brokers"
  }
];

const STAGES = ["Lead", "Discovery", "Proposal", "Negotiation", "Closed - Won", "Closed - Lost"];
const BUSINESS_TYPES = ["Retail", "Wholesale", "Food Service", "E-Commerce", "Other"];
const PRODUCT_TYPES = ["Frozen Foods", "Refrigerated", "Shelf Stable", "Beverages", "Other"];

const OpportunitiesPage = ({ navigateToOpportunity, initialFilter, clearFilter }) => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState(initialFilter || "all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newOpportunity, setNewOpportunity] = useState({
    customer_name: "",
    description: "",
    stage: "Lead",
    total_revenue: "",
    product_type: "Frozen Foods",
    business_type: "Retail",
    close_probability: 25,
    expected_close_date: ""
  });

  useEffect(() => {
    fetchOpportunities();
  }, []);

  useEffect(() => {
    if (initialFilter) {
      setStageFilter(initialFilter);
    }
  }, [initialFilter]);

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${API}/crm/opportunities`);
      setOpportunities(response.data);
    } catch (error) {
      console.error('Failed to fetch opportunities', error);
      setOpportunities(MOCK_OPPORTUNITIES);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOpportunity = async () => {
    try {
      const payload = {
        ...newOpportunity,
        total_revenue: parseFloat(newOpportunity.total_revenue) || 0
      };
      await axios.post(`${API}/crm/opportunities`, payload);
      toast.success("Opportunity created successfully");
      setShowCreateModal(false);
      setNewOpportunity({
        customer_name: "",
        description: "",
        stage: "Lead",
        total_revenue: "",
        product_type: "Frozen Foods",
        business_type: "Retail",
        close_probability: 25,
        expected_close_date: ""
      });
      fetchOpportunities();
    } catch (error) {
      console.error('Failed to create opportunity', error);
      toast.error("Failed to create opportunity");
    }
  };

  const formatRevenue = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}mm`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}k`;
    } else {
      return `$${amount.toFixed(0)}`;
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Lead': return 'bg-blue-100 text-blue-800';
      case 'Discovery': return 'bg-purple-100 text-purple-800';
      case 'Proposal': return 'bg-indigo-100 text-indigo-800';
      case 'Negotiation': return 'bg-orange-100 text-orange-800';
      case 'Closed - Won': return 'bg-green-100 text-green-800';
      case 'Closed - Lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredOpportunities = opportunities.filter(opp => {
    const matchesSearch = searchQuery === "" || 
      opp.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStage = stageFilter === "all" || opp.stage === stageFilter;
    return matchesSearch && matchesStage;
  });

  // Calculate totals
  const totalRevenue = filteredOpportunities.reduce((sum, opp) => sum + (opp.total_revenue || 0), 0);
  const probableRevenue = filteredOpportunities.reduce((sum, opp) => 
    sum + ((opp.total_revenue || 0) * (opp.close_probability || 0) / 100), 0
  );

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Opportunities</h1>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          💼 Opportunities
        </h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Opportunity
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Opportunities</div>
            <div className="text-2xl font-bold">{filteredOpportunities.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Pipeline</div>
            <div className="text-2xl font-bold text-green-600">{formatRevenue(totalRevenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Probable Revenue</div>
            <div className="text-2xl font-bold text-blue-600">{formatRevenue(probableRevenue)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search opportunities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={stageFilter} onValueChange={(val) => {
          setStageFilter(val);
          if (val === "all" && clearFilter) clearFilter();
        }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGES.map(stage => (
              <SelectItem key={stage} value={stage}>{stage}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Opportunities List */}
      <div className="space-y-3">
        {filteredOpportunities.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              No opportunities found
            </CardContent>
          </Card>
        ) : (
          filteredOpportunities.map(opp => (
            <Card 
              key={opp.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigateToOpportunity && navigateToOpportunity(opp.id)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{opp.customer_name}</h3>
                      <Badge className={getStageColor(opp.stage)}>{opp.stage}</Badge>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">{opp.description}</p>
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        {opp.product_type}
                      </span>
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {opp.business_type}
                      </span>
                      <span>📊 {opp.close_probability}% probability</span>
                      {opp.expected_close_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(opp.expected_close_date).toLocaleDateString()}
                        </span>
                      )}
                      {opp.owner_name && <span>👤 {opp.owner_name}</span>}
                      {opp.broker_name && <span>🤝 {opp.broker_name}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-green-600">{formatRevenue(opp.total_revenue)}</div>
                    <div className="text-xs text-gray-500">
                      {formatRevenue((opp.total_revenue || 0) * (opp.close_probability || 0) / 100)} probable
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Opportunity Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Opportunity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={newOpportunity.customer_name}
                onChange={(e) => setNewOpportunity({ ...newOpportunity, customer_name: e.target.value })}
                placeholder="Enter customer name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newOpportunity.description}
                onChange={(e) => setNewOpportunity({ ...newOpportunity, description: e.target.value })}
                placeholder="Describe the opportunity"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stage</Label>
                <Select 
                  value={newOpportunity.stage} 
                  onValueChange={(val) => setNewOpportunity({ ...newOpportunity, stage: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map(stage => (
                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Total Revenue ($)</Label>
                <Input
                  type="number"
                  value={newOpportunity.total_revenue}
                  onChange={(e) => setNewOpportunity({ ...newOpportunity, total_revenue: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Product Type</Label>
                <Select 
                  value={newOpportunity.product_type} 
                  onValueChange={(val) => setNewOpportunity({ ...newOpportunity, product_type: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Business Type</Label>
                <Select 
                  value={newOpportunity.business_type} 
                  onValueChange={(val) => setNewOpportunity({ ...newOpportunity, business_type: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Close Probability (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={newOpportunity.close_probability}
                  onChange={(e) => setNewOpportunity({ ...newOpportunity, close_probability: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Expected Close Date</Label>
                <Input
                  type="date"
                  value={newOpportunity.expected_close_date}
                  onChange={(e) => setNewOpportunity({ ...newOpportunity, expected_close_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateOpportunity} disabled={!newOpportunity.customer_name}>
              Create Opportunity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpportunitiesPage;
